<template>
  <div class="flex flex-col h-full overflow-hidden">
    <div :class="withStickySubmitters ? 'flex-none pb-2 pt-1 border-b border-base-300' : 'flex-none'">
      <FieldSubmitter
        :model-value="selectedSubmitter.uuid"
        class="roles-dropdown w-full rounded-lg roles-dropdown bg-transparent mb-2"
        :submitters="submitters"
        :menu-style="{ overflow: 'auto', display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 120px)', backgroundColor: ['', null, 'transparent'].includes(backgroundColor) ? 'white' : backgroundColor }"
        :editable="editable && !defaultSubmitters.length && !cohortId"
        :allow-add-new="!cohortId"
        :allow-edit-role="!!cohortId"
        @edit-role="$emit('edit-role', $event)"
        @new-submitter="save"
        @remove="removeSubmitter"
        @name-change="save"
        @update:model-value="$emit('change-submitter', submitters.find((s) => s.uuid === $event))"
      />
      <div
        v-if="editable && !onlyDefinedFields"
        id="field-types-grid"
        class="grid grid-cols-3 gap-1 pb-2 fields-grid"
      >
        <template
          v-for="(icon, type) in fieldIconsSorted"
          :key="type"
        >
          <button
            v-if="fieldTypes.includes(type) || ((withPhone || type != 'phone') && (withPayment || type != 'payment') && (withVerification || type != 'verification') && (withKba || type != 'kba'))"
            :id="`${type}_type_field_button`"
            draggable="true"
            class="field-type-button group flex items-center justify-center border border-dashed w-full rounded relative fields-grid-item bg-transparent"
            :class="drawFieldType === type ? 'border-base-content/40' : 'border-base-300 hover:border-base-content/20'"
            @dragstart="onDragstart($event, { type: type })"
            @dragend="$emit('drag-end')"
            @click="['file', 'payment', 'verification', 'kba'].includes(type) ? $emit('add-field', type) : $emit('set-draw-type', type)"
          >
            <div
              class="flex items-console transition-all cursor-grab h-full absolute left-0"
              :class="drawFieldType === type ? 'bg-base-200/50' : 'group-hover:bg-base-200/50'"
            >
              <IconDrag class="my-auto" />
            </div>
            <div class="flex items-center flex-col px-2 py-2">
              <component :is="icon" />
              <span class="text-xs mt-1">
                {{ fieldNames[type] }}
              </span>
            </div>
          </button>
          <div
            v-else-if="withVerification === false && type == 'verification' && (fieldTypes.length === 0 || fieldTypes.includes(type))"
            class="tooltip tooltip-bottom flex tooltip-bottom-start"
            :data-tip="t('obtain_qualified_electronic_signature_with_the_trusted_provider_click_to_learn_more')"
          >
            <a
              href="/legal"
              target="_blank"
              class="opacity-50 flex items-center justify-center border border-dashed border-base-300 w-full rounded relative fields-grid-item bg-transparent"
            >
              <div class="w-0 absolute left-0">
                <IconLock
                  width="18"
                  height="18"
                  stroke-width="1.5"
                />
              </div>
              <div class="flex items-center flex-col px-2 py-2">
                <component :is="icon" />
                <span class="text-xs mt-1">
                  {{ fieldNames[type] }}
                </span>
              </div>
            </a>
          </div>
        </template>
      </div>
    </div>
    <div
      ref="fields"
      class="fields mb-1 pt-6 flex-1 min-h-0 overflow-y-auto overflow-x-hidden doccu-scrollbar pr-2"
      style="-webkit-mask-image: linear-gradient(to bottom, transparent 0, black 24px, black calc(100% - 24px), transparent 100%); mask-image: linear-gradient(to bottom, transparent 0, black 24px, black calc(100% - 24px), transparent 100%);"
      @dragover.prevent="onFieldDragover"
      @drop="fieldsDragFieldRef.value ? reorderFields() : null"
    >
      <Field
        v-for="field in submitterFields"
        :key="field.uuid"
        :data-uuid="field.uuid"
        :field="field"
        :type-index="fields.filter((f) => f.type === field.type).indexOf(field)"
        :editable="editable"
        :with-signature-id="withSignatureId"
        :with-prefillable="withPrefillable"
        :default-field="defaultFieldsIndex[field.name]"
        :draggable="editable"
        @dragstart="[fieldsDragFieldRef.value = field, removeDragOverlay($event), setDragPlaceholder($event)]"
        @dragend="[fieldsDragFieldRef.value = null, $emit('set-drag-placeholder', null)]"
        @remove="removeField"
        @scroll-to="$emit('scroll-to-area', $event)"
        @set-draw="$emit('set-draw', $event)"
      />
    </div>
    <div v-if="submitterDefaultFields.length && editable">
      <hr class="mb-2">
      <template v-if="isShowFieldSearch">
        <input
          v-model="defaultFieldsSearch"
          :placeholder="t('search_field')"
          class="input input-ghost input-xs px-0 text-base mb-2 !outline-0 !rounded bg-transparent w-full"
        >
        <hr class="mb-2">
      </template>
      <div
        class="overflow-auto relative"
        :style="{
          maxHeight: isShowFieldSearch ? '210px' : '',
          minHeight: isShowFieldSearch ? '210px' : ''
        }"
      >
        <div
          v-if="!filteredSubmitterDefaultFields.length && defaultFieldsSearch"
          class="top-0 bottom-0 text-center absolute flex items-center justify-center w-full flex-col"
        >
          <div>
            {{ t('field_not_found') }}
          </div>
          <a
            href="#"
            class="link"
            @click.prevent="defaultFieldsSearch = ''"
          >
            {{ t('clear') }}
          </a>
        </div>
        <template
          v-for="field in filteredSubmitterDefaultFields"
          :key="field.name"
        >
          <div
            draggable="true"
            class="border border-base-300 rounded relative group mb-2 default-field fields-list-item bg-transparent"
            @dragstart="onDragstart($event, field)"
            @dragend="$emit('drag-end')"
          >
            <div class="flex items-center justify-between relative cursor-grab">
              <div class="flex items-center p-1 space-x-1">
                <IconDrag />
                <FieldType
                  :model-value="field.type || 'text'"
                  :editable="false"
                  :button-width="20"
                />
                <span class="block pl-0.5">
                  {{ field.title || field.name }}
                </span>
              </div>
              <span
                v-if="defaultRequiredFields.includes(field)"
                :data-tip="t('required')"
                class="text-red-400 text-3xl pr-1.5 tooltip tooltip-left h-8"
              >
                *
              </span>
            </div>
          </div>
        </template>
      </div>
    </div>

    <div
      v-if="editable && withHelp && !showTourStartForm"
      class="mt-auto relative px-1 mb-2 flex flex-col justify-end w-full box-border"
    >
      <transition
        enter-active-class="transition-all duration-300 ease-out origin-bottom-right"
        leave-active-class="transition-all duration-200 ease-in origin-bottom-right"
        enter-from-class="opacity-0 scale-50"
        enter-to-class="opacity-100 scale-100"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-50"
        mode="out-in"
      >
        <div
          v-if="isQuickTipsOpen"
          class="bg-[#043f42]/5 border border-[#043f42]/10 rounded-[12px] p-4 text-[#043f42] text-[13px] font-['Raleway'] leading-relaxed w-full shadow-sm mb-2"
        >
          <div class="flex items-center justify-between mb-3 font-semibold text-[#043f42]">
            <div class="flex items-center gap-2">
              <IconSparkles
                class="w-[18px] h-[18px] text-[#22ec9b]"
                stroke-width="2"
              />
              Quick Tips
            </div>
            <button
              class="text-[#043f42]/40 hover:text-[#043f42] transition-colors p-1 rounded-full hover:bg-[#043f42]/5"
              @click="isQuickTipsOpen = false"
            >
              <IconX class="w-4 h-4" />
            </button>
          </div>
          <ul class="flex flex-col gap-2">
            <li class="flex items-start gap-2">
              <div class="w-1.5 h-1.5 rounded-full bg-[#22ec9b] mt-[7px] shrink-0" />
              <span>{{ t('draw_a_text_field_on_the_page_with_a_mouse') }}</span>
            </li>
            <li class="flex items-start gap-2">
              <div class="w-1.5 h-1.5 rounded-full bg-[#22ec9b] mt-[7px] shrink-0" />
              <span>{{ t('drag_and_drop_any_other_field_type_on_the_page') }}</span>
            </li>
            <li class="flex items-start gap-2">
              <div class="w-1.5 h-1.5 rounded-full bg-[#22ec9b] mt-[7px] shrink-0" />
              <span>{{ t('click_on_the_field_type_above_to_start_drawing_it') }}</span>
            </li>
          </ul>
        </div>
        <div
          v-else
          class="flex justify-end w-full"
        >
          <button
            class="w-10 h-10 mb-2 bg-[#043f42]/10 hover:bg-[#043f42]/20 text-[#043f42] rounded-full flex items-center justify-center shadow-sm transition-all border border-[#043f42]/10 hover:scale-105 active:scale-95"
            @click="isQuickTipsOpen = true"
          >
            <IconInfoCircle
              class="w-[22px] h-[22px]"
              stroke-width="2"
            />
          </button>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import Field from './field'
import FieldType from './field_type'
import FieldSubmitter from './field_submitter'
import { IconLock, IconCirclePlus, IconInnerShadowTop, IconSparkles, IconX, IconInfoCircle } from '@tabler/icons-vue'
import IconDrag from './icon_drag'

export default {
  name: 'TemplateFields',
  components: {
    Field,
    FieldType,
    IconCirclePlus,
    IconSparkles,
    IconX,
    IconInfoCircle,
    IconInnerShadowTop,
    FieldSubmitter,
    IconDrag,
    IconLock
  },
  inject: ['save', 'backgroundColor', 'withPhone', 'withVerification', 'withKba', 'withPayment', 't', 'fieldsDragFieldRef', 'baseFetch', 'selectedAreasRef'],
  props: {
    fields: {
      type: Array,
      required: true
    },
    withFieldsSearch: {
      type: Boolean,
      required: false,
      default: null
    },
    withFieldsDetection: {
      type: Boolean,
      required: false,
      default: false
    },
    withSignatureId: {
      type: Boolean,
      required: false,
      default: null
    },
    withPrefillable: {
      type: Boolean,
      required: false,
      default: false
    },
    template: {
      type: Object,
      required: true
    },
    withHelp: {
      type: Boolean,
      required: false,
      default: true
    },
    editable: {
      type: Boolean,
      required: false,
      default: true
    },
    defaultFields: {
      type: Array,
      required: false,
      default: () => []
    },
    defaultRequiredFields: {
      type: Array,
      required: false,
      default: () => []
    },
    onlyDefinedFields: {
      type: Boolean,
      required: false,
      default: true
    },
    drawFieldType: {
      type: String,
      required: false,
      default: ''
    },
    defaultSubmitters: {
      type: Array,
      required: false,
      default: () => []
    },
    withStickySubmitters: {
      type: Boolean,
      required: false,
      default: true
    },
    fieldTypes: {
      type: Array,
      required: false,
      default: () => []
    },
    cohortId: {
      type: [String, Number],
      required: false,
      default: null
    },
    submitters: {
      type: Array,
      required: true
    },
    selectedSubmitter: {
      type: Object,
      required: true
    },
    showTourStartForm: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['add-field', 'set-draw', 'set-draw-type', 'set-drag', 'drag-end', 'scroll-to-area', 'change-submitter', 'set-drag-placeholder', 'select-submitter', 'edit-role'],
  data () {
    return {
      fieldPagesLoaded: null,
      analyzingProgress: 0,
      defaultFieldsSearch: '',
      isQuickTipsOpen: true
    }
  },
  computed: {
    fieldNames: FieldType.computed.fieldNames,
    fieldIcons: FieldType.computed.fieldIcons,
    numberOfPages () {
      return this.template.documents.reduce((acc, doc) => {
        return acc + doc.metadata?.pdf?.number_of_pages || doc.preview_images.length
      }, 0)
    },
    isShowFieldSearch () {
      if (this.withFieldsSearch === false) {
        return false
      } else {
        return this.submitterDefaultFields.length > 15
      }
    },
    defaultFieldsIndex () {
      return this.defaultFields.reduce((acc, field) => {
        acc[field.name] = field

        return acc
      }, {})
    },
    skipTypes () {
      return ['heading', 'datenow', 'strikethrough']
    },
    fieldIconsSorted () {
      let types = this.fieldTypes.length
        ? this.fieldTypes.slice()
        : Object.keys(this.fieldIcons).filter((key) => !this.skipTypes.includes(key))

      // In cohort programmes, file attachments are strictly learner compliance documents (Student only)
      if (this.cohortId && this.selectedSubmitter?.uuid !== 'student') {
        types = types.filter((t) => t !== 'file')
      }

      return types.reduce((acc, type) => {
        if (this.fieldIcons[type]) {
          acc[type] = this.fieldIcons[type]
        }

        return acc
      }, {})
    },
    submitterFields () {
      return this.fields.filter((f) => f.submitter_uuid === this.selectedSubmitter.uuid)
    },
    submitterDefaultFields () {
      return this.defaultFields.filter((f) => {
        return !this.submitterFields.find((field) => field.name === f.name) && (!f.role || f.role === this.selectedSubmitter.name)
      })
    },
    filteredSubmitterDefaultFields () {
      if (this.defaultFieldsSearch) {
        return this.submitterDefaultFields.filter((f) => (f.title || f.name).toLowerCase().includes(this.defaultFieldsSearch.toLowerCase()))
      } else {
        return this.submitterDefaultFields
      }
    }
  },
  methods: {
    onDragstart (event, field) {
      this.removeDragOverlay(event)

      this.setDragPlaceholder(event)

      this.$emit('set-drag', field)
    },
    detectFields () {
      const fields = []

      this.fieldPagesLoaded = 0

      this.baseFetch(`/templates/${this.template.id}/detect_fields`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      }).then(async (response) => {
        const reader = response.body.getReader()
        const decoder = new TextDecoder('utf-8')
        let buffer = ''

        while (true) {
          const { value, done } = await reader.read()

          buffer += decoder.decode(value, { stream: true })

          const lines = buffer.split('\n\n')

          buffer = lines.pop()

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const jsonStr = line.replace(/^data: /, '')
              const data = JSON.parse(jsonStr)

              if (data.error) {
                if ((data.fields || fields).length) {
                  this.template.fields = data.fields || fields

                  this.save()
                } else {
                  alert(data.error)
                }

                break
              } else if (data.analyzing) {
                this.analyzingProgress = data.progress
              } else if (data.completed) {
                this.fieldPagesLoaded = null

                if (data.submitters) {
                  this.template.submitters = data.submitters
                  this.$emit('select-submitter', this.template.submitters[0])
                }

                this.template.fields = data.fields || fields

                this.save()

                break
              } else if (data.fields) {
                data.fields.forEach((f) => {
                  if (!f.submitter_uuid) {
                    f.submitter_uuid = this.template.submitters[0].uuid
                  }
                })

                this.fieldPagesLoaded += 1

                fields.push(...data.fields)
              }
            }
          }

          if (done) break
        }
      }).catch(error => {
        console.error('Error in streaming message: ', error)
      }).finally(() => {
        this.fieldPagesLoaded = null
        this.analyzingProgress = null
        this.isFieldsLoading = false
      })
    },
    setDragPlaceholder (event) {
      this.$emit('set-drag-placeholder', {
        offsetX: event.offsetX,
        offsetY: event.offsetY,
        x: event.clientX - event.offsetX,
        y: event.clientY - event.offsetY,
        w: event.currentTarget.clientWidth + 2,
        h: event.currentTarget.clientHeight + 2
      })
    },
    removeDragOverlay (event) {
      const root = this.$el.getRootNode()
      const hiddenEl = document.createElement('div')

      hiddenEl.style.width = '1px'
      hiddenEl.style.height = '1px'
      hiddenEl.style.opacity = '0'
      hiddenEl.style.position = 'fixed'

      root.querySelector('#docuseal_modal_container').appendChild(hiddenEl)

      event.dataTransfer.setDragImage(hiddenEl, 0, 0)

      setTimeout(() => { hiddenEl.remove() }, 1000)
    },
    startTour () {
      document.querySelector('app-tour').start()
    },
    onFieldDragover (e) {
      if (this.fieldsDragFieldRef.value) {
        const targetField = e.target.closest('[data-uuid]')
        const dragField = this.$refs.fields.querySelector(`[data-uuid="${this.fieldsDragFieldRef.value.uuid}"]`)

        if (dragField && targetField && targetField !== dragField) {
          const fields = Array.from(this.$refs.fields.children)
          const currentIndex = fields.indexOf(dragField)
          const targetIndex = fields.indexOf(targetField)

          if (currentIndex < targetIndex) {
            targetField.after(dragField)
          } else {
            targetField.before(dragField)
          }
        }
      }
    },
    reorderFields () {
      Array.from(this.$refs.fields.children).forEach((el, index) => {
        if (el.dataset.uuid !== this.fields[index].uuid) {
          const field = this.fields.find((f) => f.uuid === el.dataset.uuid)

          this.fields.splice(this.fields.indexOf(field), 1)
          this.fields.splice(index, 0, field)
        }
      })

      this.save()
    },
    removeSubmitter (submitter) {
      [...this.fields].forEach((field) => {
        if (field.submitter_uuid === submitter.uuid) {
          this.removeField(field, false)
        }
      })

      this.submitters.splice(this.submitters.indexOf(submitter), 1)

      if (this.selectedSubmitter === submitter) {
        this.$emit('change-submitter', this.submitters[0])
      }

      this.save()
    },
    removeField (field, save = true) {
      this.fields.splice(this.fields.indexOf(field), 1)

      this.fields.forEach((f) => {
        (f.conditions || []).forEach((c) => {
          if (c.field_uuid === field.uuid) {
            f.conditions.splice(f.conditions.indexOf(c), 1)
          }
        })
      })

      this.template.schema.forEach((item) => {
        (item.conditions || []).forEach((c) => {
          if (c.field_uuid === field.uuid) {
            item.conditions.splice(item.conditions.indexOf(c), 1)
          }
        })
      })

      field.areas?.forEach((area) => {
        this.selectedAreasRef.value.splice(this.selectedAreasRef.value.indexOf(area), 1)
      })

      if (save) {
        this.save()
      }
    }
  }
}
</script>
